import React, { useEffect, useState } from 'react';
import { Owner, OwnerType } from '../types';
import { OwnerForm, emptyOwner } from './OwnerForm';
import { OwnerTypeTabs } from './OwnerTypeTabs';
import { FormModal } from './FormModal';

interface OwnerModalProps {
    isOpen: boolean;
    owner?: Owner;
    ownedEntityId: number;
    onSave: (owner: Owner) => Promise<void> | void;
    onClose: () => void;
}

const INSTRUCTIONS =
    'Add the owner(s) of the borrower entity by type: individual person, entity, or a group of people ' +
    '(if no single person in the group owns more than 20%).';

export const OwnerModal: React.FC<OwnerModalProps> = ({
    isOpen,
    owner,
    ownedEntityId,
    onSave,
    onClose,
}) => {
    const isEditing = !!owner && owner.id > 0;
    const [draft, setDraft] = useState<Owner>(() => owner ?? emptyOwner(OwnerType.Person, ownedEntityId));

    useEffect(() => {
        if (isOpen) {
            setDraft(owner ?? emptyOwner(OwnerType.Person, ownedEntityId));
        }
    }, [isOpen, owner, ownedEntityId]);

    const handleTypeChange = (ownerType: OwnerType) => {
        setDraft((prior) => ({
            ...emptyOwner(ownerType, ownedEntityId),
            ownershipPercent: prior.ownershipPercent,
        }));
    };

    return (
        <FormModal
            id='bpOwnerModal'
            isOpen={isOpen}
            title={isEditing ? 'Edit Owner' : 'Add Owner'}
            onSave={() => onSave(draft)}
            onClose={onClose}
        >
            {!isEditing && (
                <>
                    <p className='bp-step__instructions'>{INSTRUCTIONS}</p>
                    <div style={{ margin: '16px 0 24px' }}>
                        <OwnerTypeTabs
                            id='bpOwnerModalType'
                            value={draft.ownerType}
                            onChange={handleTypeChange}
                        />
                    </div>
                </>
            )}

            <OwnerForm
                idPrefix='bpOwnerModal'
                owner={draft}
                onChange={(patch) => setDraft((prior) => ({ ...prior, ...patch }) as Owner)}
            />
        </FormModal>
    );
};
